import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { FullPageCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/FullPageCTARenderer';
import { allOperators, standardOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { RISK_TIER_SEVERITY, RISK_TIER_SOLID_COLORS } from '../../Compounds/RiskSummaryWidget/riskTierStyles';
import { RelatedInventoryRiskItem } from '../../../Api/Projects/projectsApi';
import { DefaultInventoryGridColumnDefs, inventoryTagsColumn } from '../Inventory/inventoryGridColumnDefs';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import { TAGS_FIELD } from '../../Compounds/UpstackDataGrid/tagColumns';
import type { TagEntityRefresh } from '../../Compounds/Tagging/Tags';
import { formatDate } from '../../../utils/helpers';
import { ACTIVE_RISK_LABEL, isLiveService, riskColumnLabel } from '../../../utils/riskColumnLabel';

export const DefaultProjectsRelatedInventoryColumnDefs: GridSingleSelectColDef[] = [
  {
    field: 'details',
    headerName: 'Details',
    sortable: false,
    width: 65,
    filterable: false,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
      />
    ),
    hideable: false,
    disableReorder: true
  },
  {
    field: 'addressInfo.number',
    headerName: 'Location Code',
    sortable: true,
    width: 200,
    filterable: true,
    valueGetter: (value, row) => row?.addressInfo?.number,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'address',
    headerName: 'Address',
    sortable: true,
    width: 300,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'supplier.name',
    headerName: 'Supplier',
    valueGetter: (value, row) => row?.supplier?.name,
    sortable: true,
    width: 112,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'productDisplay',
    headerName: 'Product',
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'nextSteps',
    headerName: 'Next Steps',
    sortable: true,
    width: 130,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'focDate',
    headerName: 'FOC Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: allOperators(),
    width: 190,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column'
  },
  {
    field: 'completionDate',
    headerName: 'Install Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: allOperators(),
    width: 190,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column'
  },
  {
    field: 'billingCommencementDate',
    headerName: 'Billing Commencement Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: allOperators(),
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column'
  },
  {
    field: 'estimatedDeliveryDate',
    headerName: 'Estimated Delivery Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: allOperators(),
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column'
  },
  {
    field: 'customerRequestedDate',
    headerName: 'Customer Requested Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    filterOperators: allOperators(),
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column'
  },
  {
    field: 'circuitIDs',
    headerName: 'Circuit ID',
    sortable: true,
    width: 350,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  }
];

// Active ranks as its own group, so sorting never interleaves check marks with green dots.
const riskSortRank = (row: RelatedInventoryRiskItem | null): number => {
  if (!row?.riskTier) return 0;
  if (isLiveService(row)) return 1;
  return RISK_TIER_SEVERITY.indexOf(row.riskTier) + 2;
};

// field is riskLabel on purpose: saved layouts, pins and account column ids name it. The mark reads riskTier/riskState.
export const RelatedInventoryRiskColumnDef: GridSingleSelectColDef = {
  field: 'riskLabel',
  headerName: 'Risk',
  sortable: true,
  width: 100,
  filterable: true,
  align: 'center',
  // Flex centers the circle vertically; the default text cell baseline-aligns it above center.
  display: 'flex',
  type: 'singleSelect',
  valueOptions: [],
  valueGetter: (_value, row) => riskColumnLabel(row),
  filterOperators: standardOperators(),
  sortComparator: (_v1, _v2, params1, params2) =>
    riskSortRank(params1.api.getRow(params1.id)) - riskSortRank(params2.api.getRow(params2.id)),
  renderCell: (params: GridCellParams) => {
    const row = params.row as RelatedInventoryRiskItem;
    const { riskTier, riskLabel } = row;
    if (!riskTier) return null;
    // Outlined, and sized so the ring matches the 20px dot — the icon draws r=10 inside a 24 viewBox.
    if (isLiveService(row)) {
      return (
        <CheckCircleOutlineIcon
          titleAccess={ACTIVE_RISK_LABEL}
          sx={{ fontSize: 24, color: RISK_TIER_SOLID_COLORS.green }}
        />
      );
    }
    return (
      <span
        role="img"
        aria-label={riskLabel || riskTier}
        title={riskLabel || undefined}
        className="inline-block h-5 w-5 rounded-full"
        style={{ backgroundColor: RISK_TIER_SOLID_COLORS[riskTier] }}
      />
    );
  },
  description: 'Drag to move column'
};

const EXCLUDED_ASSET_DETAIL_FIELDS = ['details', 'id', 'detailCTA', 'sfId'];

let _defaultProjectAssetDetailFieldDefs: GroupedGridColDef[] | null = null;

// Lazily computed, not a top-level constant: DefaultInventoryGridColumnDefs pulls in
// Inventory/inventoryGridColumnDefs.tsx, which imports store/User.ts, which (via
// store/AccountCustomization.tsx) imports this very module — a circular import. Reading
// DefaultInventoryGridColumnDefs eagerly at module-evaluation time can catch it mid-load
// (temporarily undefined) depending on which side of the cycle is required first; deferring the
// read to first call (always well after app bootstrap — e.g. a component render) avoids that.
// The `?? []` guards the pathological case where this is still somehow called mid-cycle, so a
// broken require ordering degrades to the Projects-only subset instead of crashing.
export const getDefaultProjectAssetDetailFieldDefs = (): GroupedGridColDef[] => {
  if (_defaultProjectAssetDetailFieldDefs) return _defaultProjectAssetDetailFieldDefs;

  const inventoryDefsLoaded = Array.isArray(DefaultInventoryGridColumnDefs);
  const calculatedDefs = [...DefaultProjectsRelatedInventoryColumnDefs, ...(DefaultInventoryGridColumnDefs ?? [])]
    .reduce((defs, col): GroupedGridColDef[] => {
      if (!defs.some((d) => d.field === col.field)) defs.push(col);
      return defs;
    }, [] as GroupedGridColDef[])
    .filter((col) => !EXCLUDED_ASSET_DETAIL_FIELDS.includes(col.field));

  // If inventory defs are still unavailable mid circular-import, return a
  // degraded subset without caching so later calls can recover fully.
  if (!inventoryDefsLoaded) return calculatedDefs;

  _defaultProjectAssetDetailFieldDefs = calculatedDefs;
  return _defaultProjectAssetDetailFieldDefs;
};

let _relatedInventoryColumnDefs: GroupedGridColDef[] | null = null;

const RELATED_INVENTORY_HIDDEN_FIELDS = ['deviceDetails'];

// Related-inventory rows are inventory assets, so the grid offers the full Inventory catalog. The
// few columns Inventory doesn't carry (Next Steps + the fulfillment dates) are appended from the RI
// defs so nothing the grid historically offered is dropped. Lazy + memoized for the same
// circular-import reason as getDefaultProjectAssetDetailFieldDefs (DefaultInventoryGridColumnDefs
// transitively imports this module); a mid-cycle call degrades to the RI-only subset without caching.
const relatedInventoryColumnDefs = (): GroupedGridColDef[] => {
  if (_relatedInventoryColumnDefs) return _relatedInventoryColumnDefs;

  const inventoryLoaded = Array.isArray(DefaultInventoryGridColumnDefs);
  const inventoryDefs = (DefaultInventoryGridColumnDefs ?? []).map((col) =>
    RELATED_INVENTORY_HIDDEN_FIELDS.includes(col.field) ? { ...col, defaultHidden: true } : col
  );
  const inventoryFields = new Set(inventoryDefs.map((col) => col.field));
  const riOnlyDefs = DefaultProjectsRelatedInventoryColumnDefs.filter((col) => !inventoryFields.has(col.field));
  const combined = [...inventoryDefs, ...riOnlyDefs];

  if (!inventoryLoaded) return combined;
  _relatedInventoryColumnDefs = combined;
  return _relatedInventoryColumnDefs;
};

// The Risk column is grid-only: the base defs also seed custom-view initial state and the drawer/1:1
// detail field list, where a plain-text Risk row would be wrong.
// `refetchTaggedRow` re-homes where a tag edit is re-read from; omitted, the Inventory catalog's refresh stands.
export const projectsRelatedInventoryColumns = (
  includeRisk: boolean,
  refetchTaggedRow?: TagEntityRefresh
): GroupedGridColDef[] => {
  const catalog = relatedInventoryColumnDefs();
  const defs = refetchTaggedRow
    ? catalog.map((col) => (col.field === TAGS_FIELD ? inventoryTagsColumn(refetchTaggedRow) : col))
    : catalog;
  if (!includeRisk) return defs;

  const riskColumnIndex = defs.findIndex((col) => col.field === 'details') + 1;
  return [...defs.slice(0, riskColumnIndex), RelatedInventoryRiskColumnDef, ...defs.slice(riskColumnIndex)];
};

// Inventory mode's rows span every project, so a row's detail lives on its own project's page
// addressed to that asset — not at this page's path plus the row id, and not in a drawer.
const renderProjectAssetDetailCTA = (params: GridCellParams) => {
  const { id, projectId } = params.row as RelatedInventoryRiskItem;
  if (!projectId) return null;

  return (
    <FullPageCTARenderer
      data={params}
      value={''}
      valueFormatted={''}
      path={`/projects/${encodeURIComponent(projectId)}?assetId=${encodeURIComponent(id)}`}
    />
  );
};

// The Related Inventory catalog as the Projects page's Inventory mode uses it: identical columns —
// the account's own related-inventory configuration governs both — with only the Details CTA re-aimed.
export const projectsInventoryModeColumns = (
  includeRisk: boolean,
  refetchTaggedRow?: (id: string) => Promise<void>
): GroupedGridColDef[] =>
  projectsRelatedInventoryColumns(includeRisk, refetchTaggedRow).map((col) =>
    col.field === 'details' ? { ...col, renderCell: renderProjectAssetDetailCTA } : col
  );

export { createCustomFieldColumns } from '../../Compounds/UpstackDataGrid/customFieldColumns';
